import { InfoIcon } from "@chakra-ui/icons";
import { Badge, HStack, Text, Tooltip } from "@chakra-ui/react";
import { LocalDate } from "@js-joda/core";
import { auth } from "../../../../../core/auth";
import type { CaregiverId } from "../../../../../shared/schema/schema";
import EntityModalButton from "../../../../entityProfileModal/components/EntityModalButton";
import EntityProfileDataTable from "../../../../entityProfileModal/components/EntityProfileDataTable";
import EntityProfileQuerySection from "../../../../entityProfileModal/components/EntityProfileQuerySection";
import { EntityProfileSummaryBlock as SummaryBlock } from "../../../../entityProfileModal/components/EntityProfileSummary";
import { entityProfileModalColors } from "../../../../entityProfileModal/entityProfileModal.theme";
import { getEntityStatusColorScheme } from "../../../../../shared/utils/get-entity-status-color-scheme";
import { useCaregiverStatusHistory } from "./CaregiverProfileStatusReviewSection.hooks";
import {
  formatStatusReviewDate,
  formatStatusReviewNote,
  formatStatusReviewShortDate,
  formatStatusReviewUser,
} from "./CaregiverProfileStatusReviewSection.utils";

type Props = {
  caregiverId: CaregiverId;
};

export default function CaregiverProfileStatusReviewSection(props: Props) {
  const { deleteFutureStatus, isDeletingFutureStatus, statusHistoryQuery } =
    useCaregiverStatusHistory(props.caregiverId);
  const canEdit = auth.isAllowedTo("edit_caregiver_page_about_details");
  const rows = statusHistoryQuery.data?.rows ?? [];

  return (
    <SummaryBlock title="Status review">
      <EntityProfileQuerySection
        errorLabel="Could not load status review."
        isError={statusHistoryQuery.isError}
        isLoading={statusHistoryQuery.isLoading}
        loadingLabel="Loading status review"
        onRetry={() => void statusHistoryQuery.refetch()}
      >
        <EntityProfileDataTable
          columns={[
            {
              header: "Status",
              key: "status",
              render: (row) => (
                <Badge
                  borderRadius="2px"
                  colorScheme={getEntityStatusColorScheme(row.status.id)}
                  fontSize="11px"
                  px={2}
                  py={1}
                >
                  {row.status.value}
                </Badge>
              ),
              sortKey: "status",
              sortValue: (row) => row.status.value,
            },
            {
              cellProps: { fontWeight: 700 },
              header: "Effective date",
              key: "effectiveDate",
              render: (row) => formatStatusReviewDate(row.effectiveDate),
              sortKey: "effectiveDate",
              sortValue: (row) => String(row.effectiveDate ?? ""),
            },
            {
              cellProps: { maxW: "420px" },
              header: "Note",
              key: "note",
              render: (row) => (
                <Text color={entityProfileModalColors.muted} whiteSpace="pre-wrap">
                  {formatStatusReviewNote(row)}
                </Text>
              ),
              sortKey: "note",
              sortValue: (row) => formatStatusReviewNote(row),
            },
            {
              header: "Changed By",
              key: "changedBy",
              render: (row) => formatStatusReviewUser(row),
              sortKey: "changedBy",
              sortValue: (row) => formatStatusReviewUser(row),
            },
            {
              header: "Changed at",
              key: "changedAt",
              render: (row) => formatStatusReviewShortDate(row.createdAt),
              sortKey: "changedAt",
              sortValue: (row) => String(row.createdAt ?? ""),
            },
            {
              header: "",
              key: "actions",
              render: (row) =>
                canEdit ? (
                  <HStack spacing={2}>
                    <EntityModalButton
                      aria-label="Remove future status change"
                      disabled={
                        isDeletingFutureStatus ||
                        !(row.effectiveDate instanceof LocalDate) ||
                        !row.effectiveDate.isAfter(LocalDate.now())
                      }
                      iconClassName="fa fa-trash"
                      kind="red"
                      size="xs"
                      title="Remove future status change"
                      onClick={() => deleteFutureStatus(row)}
                    />
                    {!(row.effectiveDate instanceof LocalDate) ||
                    !row.effectiveDate.isAfter(LocalDate.now()) ? (
                      <Tooltip label="Only future statuses can be deleted">
                        <InfoIcon color={entityProfileModalColors.muted} />
                      </Tooltip>
                    ) : null}
                  </HStack>
                ) : null,
            },
          ]}
          emptyLabel="No status review records."
          getRowKey={(row) => row.id}
          pageSizeOptions={[10, 25, 50, 100]}
          rows={rows}
        />
      </EntityProfileQuerySection>
    </SummaryBlock>
  );
}
